import React from 'react';

export const SettlementPopup = ({
  show,
  onClose,
  onSubmit,
  isProcessing,
  processingMessage,
  settlementDates,
  setSettlementDates
}) => {
  if (!show) return null;

  const today = new Date();
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(today.getDate() - 14);

  const maxDate = today.toISOString().split('T')[0];
  const minDate = fourteenDaysAgo.toISOString().split('T')[0];

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isDateValid = (dateString) => {
    const selectedDate = new Date(dateString);
    return selectedDate >= fourteenDaysAgo && selectedDate <= today;
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    if (isDateValid(newDate) || newDate === '') {
      setSettlementDates({ fromDate: newDate });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit('settlement', settlementDates);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all animate-slideIn">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Settlement Process</h3>
                <p className="text-blue-100 text-xs">Last 14 days only</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white/10"
              disabled={isProcessing}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Date Info Banner */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">Date Range Restriction</p>
                <p className="text-xs text-blue-600 mt-1">
                  {formatDateForDisplay(minDate)} → {formatDateForDisplay(maxDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Date Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Settlement Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={settlementDates.fromDate}
              onChange={handleDateChange}
              min={minDate}
              max={maxDate}
              className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm transition-all
                ${!isDateValid(settlementDates.fromDate) && settlementDates.fromDate
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 bg-white'
                }`}
              required
              disabled={isProcessing}
            />
          </div>

          {/* Quick Select Buttons */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setSettlementDates({ fromDate: maxDate })}
              className="flex-1 text-xs px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              disabled={isProcessing}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setSettlementDates({ fromDate: yesterday.toISOString().split('T')[0] });
              }}
              className="flex-1 text-xs px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              disabled={isProcessing}
            >
              Yesterday
            </button>
          </div>

          {/* Processing Status */}
          {isProcessing && (
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 mb-4 border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-6 h-6 border-2 border-blue-200 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-6 h-6 border-2 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">{processingMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 px-4 py-2.5 text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50
                ${isDateValid(settlementDates.fromDate)
                  ? 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600'
                  : 'bg-gradient-to-r from-gray-400 to-gray-500 cursor-not-allowed'
                }`}
              disabled={isProcessing || !settlementDates.fromDate || !isDateValid(settlementDates.fromDate)}
            >
              {isProcessing ? 'Processing...' : 'Run Settlement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};